/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable arrow-body-style */
/* eslint-disable comma-dangle */

'use client';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { ChatFile, Icon, VersionValue } from './NewTypes';
import { ChatLine } from './ChatLine';
import './Chat.scss';

interface ChatProps {
    vodId: string;
    onTimestampClicked: (timestamp: number) => void;
    chat: ChatFile;
    badgeMap: Map<string, VersionValue>;
    emoteMap: Map<string, Icon>;
}

export interface ChatRef {
    setCurrentTimestamp: (timestamp: number) => void;
}

const Chat = forwardRef<ChatRef, ChatProps>(({ vodId, chat, onTimestampClicked, badgeMap, emoteMap }, ref) => {
    const [scrollLock, setScrollLock] = useState(true);
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [visibleComments, setVisibleComments] = useState<ChatFile['comments']>([]);

    useImperativeHandle(ref, () => ({
        setCurrentTimestamp: (timestamp: number) => {
            if (!(chat.comments ?? []).length || !scrollLock) {
                return;
            }

            // Binary search for the first comment after the timestamp
            /*
            const windowSize = 300;
            let left = 0;
            let right = chat.comments.length - 1;
            let start = left;
            while (left <= right) {
                const mid = Math.floor((left + right) / 2);
                if (chat.comments[mid].content_offset_seconds < timestamp - windowSize) {
                    left = mid + 1;
                } else {
                    right = mid - 1;
                }
            }
            start = left;
            start = Math.max(0, start); // show a few messages before
            start = Math.min(chat.comments.length - 1, start);
            */

            // Time went forward, check if we need to load more comments
            const vComments = visibleComments ?? [];
            if (timestamp >= (vComments[vComments.length - 1]?.content_offset_seconds ?? 0)) {
                let lastIdx = -1;

                const chatComments = chat.comments ?? [];
                for (let i = vComments.length; i < chatComments.length; i += 1) {
                    if (chatComments[i].content_offset_seconds <= timestamp) {
                        lastIdx = i;
                    } else {
                        break;
                    }
                }

                if (lastIdx >= 0) {
                    setVisibleComments(prev => [
                        ...(prev ?? []),
                        ...chatComments.slice((prev ?? []).length, lastIdx + 1),
                    ]);
                }
            } else {
                // Time went backward, find the last comment before the timestamp
                let lastIdx = -1;

                for (let i = vComments.length - 1; i >= 0; i -= 1) {
                    if (vComments[i].content_offset_seconds <= timestamp) {
                        lastIdx = i;
                        break;
                    }
                }

                if (lastIdx >= 0) {
                    setVisibleComments(prev => (prev ?? []).slice(0, lastIdx + 1));
                } else {
                    setVisibleComments([]);
                }
            }

            // Scroll to the comment
            // virtuosoRef.current?.scrollToIndex({ index: start, align: 'end', behavior: 'smooth' });
        }
    }), [chat, scrollLock, visibleComments]);

    const alreadyPrefetched = useRef<string[]>([]);
    useEffect(() => {
        const lookahead = 50;
        const chatComments = chat.comments ?? [];
        const vComments = visibleComments ?? [];
        const upcoming = chatComments.slice(vComments.length, vComments.length + lookahead);
        if (upcoming.length === 0) {
            return;
        }

        upcoming.forEach(c => {
            c.message.fragments.forEach(frag => {
                if (frag.emoticon) {
                    const emote = emoteMap.get(frag.emoticon.emoticon_id);
                    if (emote && !alreadyPrefetched.current.includes(emote.id)) {
                        const img = new Image();
                        img.src = `/api/vods/emotes/${vodId}/${emote.id}`;
                        alreadyPrefetched.current.push(emote.id);
                    }
                }
            });
        });
    }, [visibleComments, chat, emoteMap, vodId]);

    return (
        <Virtuoso
          ref={virtuosoRef}
          className="chat"
          followOutput={scrollLock ? 'auto' : false}
          atBottomStateChange={(atBottom) => { setScrollLock(atBottom); }}
          data={visibleComments}
          itemContent={(_index, line) => (
              <ChatLine
                vodId={vodId}
                line={line}
                onTimestampClicked={onTimestampClicked}
                badgeMap={badgeMap}
                emoteMap={emoteMap}
              />
          )}
        />
    );
});

Chat.displayName = 'Chat';

export { Chat };
